import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Auth } from "aws-amplify";
import {
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "../../styles/login.css";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasssction, setShowPasssction] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get("role") || "buyer";
  const isForced = searchParams.get("forced") === "true";

  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [searchParams]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUppercase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowercase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSymbols) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!isForced && !verificationCode.trim()) {
      setError("Verification code is required");
      return;
    }
    if (!newPassword) {
      setError("New password is required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      if (isForced) {
        setMessage(
          "Please return to login and enter your new password when prompted."
        );
        setTimeout(() => {
          navigate(`/login/${role}?passwordChanged=true`);
        }, 2000);
      } else {
        await Auth.forgotPasswordSubmit(email, verificationCode, newPassword);
        setMessage("Password reset successfully! Redirecting to login...");

        setTimeout(() => {
          navigate(`/login/${role}`);
        }, 2000);
      }
    } catch (err) {
      console.error("Reset password error:", err);
      if (err.code === "CodeMismatchException") {
        setError(
          "Invalid verification code. Please check your email and try again."
        );
      } else if (err.code === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new code.");
      } else if (err.code === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email.trim()) {
      setError("Email is required to resend code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await Auth.forgotPassword(email);
      setMessage("New verification code sent to your email.");
    } catch (err) {
      console.error("Resend code error:", err);
      setError("Failed to resend verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-password">
       <div className="password-form card">
        <form onSubmit={handleSubmit}>
        <div>
          <h1>{isForced ? "Change Required Password" : "Set New Password"}</h1>
          <p className="text-muted text-center" style={{ fontSize: "0.9rem" }}>
            {isForced
              ? "You must change your password before continuing. Enter your new password below."
              : "Enter the verification code sent to your email and your new password."}
          </p>
        </div>

        {error && (
          <Alert severity="error" >
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success">
            {message}
          </Alert>
        )}

        <div>
          <label htmlFor="email">Email Address</label>
          <TextField
            type="email"
            id="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
           
          />
        </div>

        {!isForced && (
          <div>
            <label htmlFor="verificationCode">Verification Code</label>
            <TextField
              type="text"
              id="verificationCode"
              fullWidth
              variant="outlined"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter 6-digit code"
              style={{ color: "#de6b14" }}
            />
            <div style={{ textAlign: "right", marginTop:'10px'}}>
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={loading}
                sx={{
                  backgroundColor: "#de6b14 !important",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  color:'#fff'

                }}
              >
                Resend Code
              </Button>
            </div>
          </div>
        )}
       
            <div>
              <label htmlFor="newPassword">New Password</label>
              <TextField
                type={showPassword ? "text" : "password"}
                id="newPassword"
                fullWidth
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
               
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <TextField
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                fullWidth
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div >
              <small className="text-muted">
                Password requirements:
                <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                  <li>At least 8 characters</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                  <li>One special character</li>
                </ul>
              </small>
            </div>
            <div >
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: "#de6b14",
                  color: "white",
                   textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#c55a11",
                  },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Reset Password"
                )}
              </Button>
            </div><div className="text-center">
              <Button
                variant="text"
                onClick={() => navigate(`/login/${role}`)}
                sx={{
                  color: "#333 !important",
                  textTransform: "none",
                  fontWeight: "500",
                  
                  "&:hover": {
                    color: "#de6b14",
                    backgroundColor: "rgba(222, 107, 20, 0.04)",
                  },
                }}
              >
                Back to Login
              </Button>
            </div>
        

      </form>
       </div>
      
    </div>
  );
};

export default ResetPassword;